import { motion } from "framer-motion";
import { TrendingUp, Users, Globe2, Lightbulb } from "lucide-react";

const stats = [
  {
    icon: Globe2,
    value: "50+",
    label: "Countries",
    description: "Global reach across continents",
  },
  {
    icon: Users,
    value: "10,000+",
    label: "Alumni Network",
    description: "Connected professionals worldwide",
  },
  {
    icon: TrendingUp,
    value: "200+",
    label: "Partner Institutions",
    description: "Universities and organizations",
  },
  {
    icon: Lightbulb,
    value: "500+",
    label: "Programs",
    description: "Educational opportunities annually",
  },
];

export const GlobalImpact = () => {
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15) 0%, rgba(6, 11, 25, 1) 50%, rgba(226, 199, 146, 0.1) 100%)'
      }}
    >
      {/* Background elements */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[180px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="text-sm uppercase tracking-widest font-semibold text-primary mb-4 block">
            Global Impact
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
            data-testid="impact-heading"
          >
            Connecting the World Through Education
          </h2>
          <p className="text-lg text-white/60 leading-relaxed">
            Our impact spans continents, fostering knowledge transfer, innovation, 
            and talent development on a global scale.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`stat-card-${index}`}
              className="text-center p-8 rounded-2xl glass-card card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <stat.icon className="w-7 h-7 text-primary" />
              </div>
              <div className="text-4xl md:text-5xl font-heading font-black gradient-text mb-2">
                {stat.value}
              </div>
              <div className="text-lg font-semibold text-white mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-white/50">
                {stat.description}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 text-center"
        >
          <p className="text-xl md:text-2xl text-white/60 font-light italic max-w-3xl mx-auto">
            "Empowering individuals and institutions to thrive in an interconnected world 
            through transformative educational experiences."
          </p>
        </motion.div>
      </div>
    </section>
  );
};
